import type { DataFetchParams } from '@/components/DataTable';
import {
  AlertsSortField,
  OrganizationScanAlertsSortField,
  ORGANIZATION_ALERTS_FILTERABLE_FIELDS,
} from '@kirin/api-client/contracts/alerts';

// Both ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES and
// USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES hold the same string
// ('clientTypes'); we reference the org constant here so the util compiles
// against the contract instead of a bare literal.
const CLIENT_TYPES_FIELD = ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES;
const CLIENT_TYPES_SORT_FIELD = AlertsSortField.ClientTypes;
const USERS_FIELD = ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS;
const ENDPOINTS_FIELD = ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS;
const ENDPOINT_COUNT_SORT_FIELD = OrganizationScanAlertsSortField.EndpointCount;

/**
 * Strip persisted filters/sort whose column is currently gated off so URL
 * state from a prior session can't bypass the gate (and the cache key stays
 * stable). When every gate is open the params pass through untouched.
 *
 * Two independent gates:
 * - `ideColumnEnabled` (feature flag) — drops the `clientTypes` filter + sort.
 * - `canFilterByUser` (KIR-3266, owner/manager only) — drops the `users`
 *   *filter*. The members-list endpoint that backs the Users filter options is
 *   RBAC-gated to owner/manager, so a stale `users` filter from a member's URL
 *   must never reach the server. Sort is left intact: a member keeps the
 *   sortable Users column (sort-by-userCount does not hit the members route).
 * - `endpointsColumnEnabled` (KIR-3035 `endpoint_identifier` flag) — drops the
 *   `endpoints` filter + `endpointCount` sort. With the flag off the column is
 *   hidden, so a stale URL must not still filter/sort by endpoint identity.
 *
 * Shared by the org alerts table and the user-dashboard alerts table — both
 * wire the same logic through the DataTable's `queryFn` /
 * `getQueryKeysFromParams`. The user-dashboard table has no Users filter, so
 * `canFilterByUser` defaults to `true` (no-op) for that caller.
 */
export function sanitizeAlertsParams(
  params: DataFetchParams,
  ideColumnEnabled: boolean,
  canFilterByUser: boolean = true,
  endpointsColumnEnabled: boolean = true
): DataFetchParams {
  if (ideColumnEnabled && canFilterByUser && endpointsColumnEnabled)
    return params;
  const filters = params.filters.filter(
    f =>
      (ideColumnEnabled || f.field !== CLIENT_TYPES_FIELD) &&
      (canFilterByUser || f.field !== USERS_FIELD) &&
      (endpointsColumnEnabled || f.field !== ENDPOINTS_FIELD)
  );
  const droppedSort =
    (!ideColumnEnabled && params.sort?.field === CLIENT_TYPES_SORT_FIELD) ||
    (!endpointsColumnEnabled &&
      params.sort?.field === ENDPOINT_COUNT_SORT_FIELD);
  const sort = droppedSort ? undefined : params.sort;
  return { ...params, filters, sort };
}
